/**
 * Servicio del modulo Atencion al Cliente (ATC).
 * Todos los endpoints pasan por api-central (proxy) bajo /atc/...
 */
import api from "./api";

// ------------------------------------------------------------
// Tipos
// ------------------------------------------------------------

export type EstadoCita =
  | "AGENDADA"
  | "EN_ESPERA"
  | "EN_ATENCION"
  | "PAUSADA"
  | "TERMINADA"
  | "CANCELADA"
  | "CANCELADA_NO_SHOW"
  | "CANCELADA_REAGENDADA";

export type ResultadoCita = "RESUELTA" | "DERIVADA" | "REQUIERE_SEGUIMIENTO" | null;

export interface Motivo {
  id: number;
  nombre: string;
  dpto_sugerido_id: number | null;
  dpto_sugerido_nombre: string | null;
  sensible: boolean;
}

export interface DptoHabilitado {
  grupo_id: number;
  grupo_nombre: string;
  nombre_visible: string;
}

export interface Cita {
  id: number;
  motivo: string;
  motivo_sensible: boolean;
  dpto_destino_id: number;
  dpto_destino_nombre: string;
  fecha_hora_agendada: string;
  observaciones: string;
  estado: EstadoCita;
  resultado: ResultadoCita;
  creado_en: string;
  cita_predecesora_id: number | null;
  cita_origen_id: number | null;
}

export interface Feedback {
  enviado_en: string;
  calificacion_general: number;
  calificacion_responsable: number;
  se_resolvio: boolean;
  comentario: string;
}

export interface ApiOk<T> {
  ok: true;
  data: T;
}
export interface ApiErr {
  ok: false;
  error: string;
}

// ------------------------------------------------------------
// Helpers
// ------------------------------------------------------------

const unwrap = <T,>(payload: ApiOk<T> | ApiErr): T => {
  if (!payload.ok) {
    throw new Error(payload.error || "Error desconocido");
  }
  return payload.data;
};

// ------------------------------------------------------------
// Catalogos
// ------------------------------------------------------------

export const listarMotivos = async (): Promise<Motivo[]> => {
  const { data } = await api.get<ApiOk<Motivo[]> | ApiErr>("atc/motivos");
  return unwrap(data);
};

export const listarDptos = async (): Promise<DptoHabilitado[]> => {
  const { data } = await api.get<ApiOk<DptoHabilitado[]> | ApiErr>("atc/dptos");
  return unwrap(data);
};

export const listarSlotsDisponibles = async (
  dpto_id: number,
  fecha: string,
): Promise<string[]> => {
  const { data } = await api.get<ApiOk<{ slots: string[] }> | ApiErr>(
    "atc/slots-disponibles",
    { params: { dpto_id, fecha } },
  );
  return unwrap(data).slots;
};

// ------------------------------------------------------------
// Citas
// ------------------------------------------------------------

export interface CrearCitaBody {
  motivo_id: number;
  dpto_destino_id: number;
  fecha_hora_agendada: string;
  observaciones?: string;
}

export const crearCita = async (body: CrearCitaBody): Promise<Cita> => {
  const { data } = await api.post<ApiOk<Cita> | ApiErr>("atc/citas", body);
  return unwrap(data);
};

export const listarMisCitas = async (params?: {
  estado?: EstadoCita;
  desde?: string;
  hasta?: string;
}): Promise<Cita[]> => {
  const { data } = await api.get<ApiOk<Cita[]> | ApiErr>("atc/citas/mias", { params });
  return unwrap(data);
};

export const obtenerCita = async (cita_id: number): Promise<Cita> => {
  const { data } = await api.get<ApiOk<Cita> | ApiErr>(`atc/citas/${cita_id}`);
  return unwrap(data);
};

export const cancelarCita = async (cita_id: number, motivo?: string): Promise<Cita> => {
  const { data } = await api.post<ApiOk<Cita> | ApiErr>(
    `atc/citas/${cita_id}/cancelar`,
    { motivo: motivo || "" },
  );
  return unwrap(data);
};

export const reagendarCita = async (
  cita_id: number,
  nueva_fecha_iso: string,
  motivo?: string,
): Promise<Cita> => {
  const { data } = await api.post<ApiOk<Cita> | ApiErr>(
    `atc/citas/${cita_id}/reagendar`,
    { fecha_hora_agendada: nueva_fecha_iso, motivo: motivo || "" },
  );
  return unwrap(data);
};

export const subirAdjunto = async (cita_id: number, file: File): Promise<unknown> => {
  const form = new FormData();
  form.append("archivo", file);
  const { data } = await api.post<ApiOk<unknown> | ApiErr>(
    `atc/citas/${cita_id}/adjuntos`,
    form,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return unwrap(data);
};

// ------------------------------------------------------------
// Feedback
// ------------------------------------------------------------

export interface FeedbackBody {
  calificacion_general: number;
  calificacion_responsable: number;
  se_resolvio: boolean;
  comentario?: string;
}

export const enviarFeedback = async (cita_id: number, body: FeedbackBody): Promise<Feedback> => {
  const { data } = await api.post<ApiOk<Feedback> | ApiErr>(
    `atc/citas/${cita_id}/feedback`,
    body,
  );
  return unwrap(data);
};

export const obtenerFeedback = async (cita_id: number): Promise<Feedback | null> => {
  try {
    const { data } = await api.get<ApiOk<Feedback> | ApiErr>(`atc/citas/${cita_id}/feedback`);
    return unwrap(data);
  } catch (err: unknown) {
    const e = err as { response?: { status?: number } };
    if (e?.response?.status === 404) return null;
    throw err;
  }
};

// ------------------------------------------------------------
// Etiquetas para UI
// ------------------------------------------------------------

export const etiquetaEstado = (e: EstadoCita): string => {
  const map: Record<EstadoCita, string> = {
    AGENDADA: "Agendada",
    EN_ESPERA: "Esperando",
    EN_ATENCION: "En atencion",
    PAUSADA: "Pausada",
    TERMINADA: "Terminada",
    CANCELADA: "Cancelada",
    CANCELADA_NO_SHOW: "No se presento",
    CANCELADA_REAGENDADA: "Reagendada",
  };
  return map[e] || e;
};

export const colorEstado = (e: EstadoCita): string => {
  const map: Record<EstadoCita, string> = {
    AGENDADA: "medium",
    EN_ESPERA: "warning",
    EN_ATENCION: "primary",
    PAUSADA: "warning",
    TERMINADA: "success",
    CANCELADA: "danger",
    CANCELADA_NO_SHOW: "danger",
    CANCELADA_REAGENDADA: "medium",
  };
  return map[e] || "medium";
};
